import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { collection, getDocs, doc, getDoc, updateDoc } from 'firebase/firestore';
import { sendEmailVerification } from 'firebase/auth';
import { db, auth } from '../firebase';
import AdminUserManagement from './AdminUserManagement';
import AdminLogs from './AdminLogs';
import AdminSettings from './AdminSettings';
import LeaderboardPoints from './LeaderboardPoints';
import Leaderboard from './Leaderboard';

interface UserStats {
  totalUsers: number;
  students: number;
  teachers: number;
  admins: number;
  activeUsers: number;
}

interface SystemStats {
  totalLessons: number;
  totalPracticeSessions: number;
  totalAwards: number;
  totalSavedSessions: number;
  totalTipResponses: number;
  averageSessionDuration: number;
  totalPracticeTime: number; // in minutes
}

const AdminDashboard: React.FC = () => {
  const { currentUser, logout } = useAuth();
  const navigate = useNavigate();
  const [currentView, setCurrentView] = useState<'dashboard' | 'users' | 'logs' | 'settings' | 'leaderboard' | 'leaderboardPoints'>('dashboard');
  const [userStats, setUserStats] = useState<UserStats>({
    totalUsers: 0,
    students: 0,
    teachers: 0,
    admins: 0,
    activeUsers: 0
  });
  const [systemStats, setSystemStats] = useState<SystemStats>({
    totalLessons: 0,
    totalPracticeSessions: 0,
    totalAwards: 0,
    totalSavedSessions: 0,
    totalTipResponses: 0,
    averageSessionDuration: 0,
    totalPracticeTime: 0
  });
  const [loading, setLoading] = useState(true);
  const [emailVerificationSent, setEmailVerificationSent] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (currentUser?.role !== 'admin') {
      navigate('/addminn');
      return;
    }
    loadStats();
  }, [currentUser, navigate]);

  const loadStats = async () => {
    try {
      setLoading(true);
      console.log('Loading admin statistics...');

      // Check if current user has admin role
      if (currentUser) {
        const userDoc = await getDoc(doc(db, 'users', currentUser.uid));
        if (userDoc.exists()) {
          const userData = userDoc.data();
          // Current user role checked
          if (userData.role !== 'admin') {
            console.error('User is not an admin:', userData.role);
            setError('Access denied. Admin role required.');
            return;
          }
        } else {
          console.error('User document not found for:', currentUser.uid);
          setError('User document not found. Please contact support.');
          return;
        }
      }

      // Load user statistics
      console.log('Loading users collection...');
      const usersSnapshot = await getDocs(collection(db, 'users'));
      const users = usersSnapshot.docs.map(doc => doc.data());
      console.log('Loaded users:', users.length);
      
      const stats: UserStats = {
        totalUsers: users.length,
        students: users.filter(user => user.role === 'student').length,
        teachers: users.filter(user => user.role === 'teacher').length,
        admins: users.filter(user => user.role === 'admin').length,
        activeUsers: users.filter(user => {
          // Consider users active if they have logged in within the last 30 days
          const lastLogin = user.lastLogin?.toDate?.() || new Date(0);
          const thirtyDaysAgo = new Date();
          thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
          return lastLogin > thirtyDaysAgo;
        }).length
      };

      setUserStats(stats);

      // Load system statistics with individual error handling
      console.log('Loading lessons collection...');
      const lessonsSnapshot = await getDocs(collection(db, 'lessons'));
      console.log('Loaded lessons:', lessonsSnapshot.docs.length);
      
      console.log('Loading practiceSessions collection...');
      const sessionsSnapshot = await getDocs(collection(db, 'practiceSessions'));
      console.log('Loaded practice sessions:', sessionsSnapshot.docs.length);
      
      console.log('Loading savedSessions collection...');
      const savedSessionsSnapshot = await getDocs(collection(db, 'savedSessions'));
      console.log('Loaded saved sessions:', savedSessionsSnapshot.docs.length);
      
      console.log('Loading tipResponses collection...');
      const tipResponsesSnapshot = await getDocs(collection(db, 'tipResponses'));
      console.log('Loaded tip responses:', tipResponsesSnapshot.docs.length);

      // Calculate awards from user badges (if users have badge data)
      const totalAwards = users.reduce((total, user) => {
        // Check if user has earnedBadges or similar award data
        const userAwards = user.earnedBadges?.length || 0;
        return total + userAwards;
      }, 0);

      // Calculate practice session statistics
      const sessionsData = sessionsSnapshot.docs.map(doc => doc.data());
      const totalPracticeTimeMinutes = sessionsData.reduce((total, session) => {
        const durationSec = session.durationSec || 0;
        return total + (durationSec / 60); // Convert seconds to minutes
      }, 0);

      const averageSessionDuration = sessionsSnapshot.docs.length > 0 
        ? totalPracticeTimeMinutes / sessionsSnapshot.docs.length 
        : 0;

      const systemStats: SystemStats = {
        totalLessons: lessonsSnapshot.docs.length + savedSessionsSnapshot.docs.length, // Include saved practice sessions as "lessons"
        totalPracticeSessions: sessionsSnapshot.docs.length,
        totalAwards: totalAwards,
        totalSavedSessions: savedSessionsSnapshot.docs.length,
        totalTipResponses: tipResponsesSnapshot.docs.length,
        averageSessionDuration: Math.round(averageSessionDuration * 10) / 10, // Round to 1 decimal place
        totalPracticeTime: Math.round(totalPracticeTimeMinutes)
      };

      setSystemStats(systemStats);
      console.log('Admin stats loaded successfully:', { stats, systemStats });
    } catch (error) {
      console.error('Error loading admin stats:', error);
      // Set default stats when permissions are not available
      setSystemStats({
        totalLessons: 0,
        totalPracticeSessions: 0,
        totalAwards: 0,
        totalSavedSessions: 0,
        totalTipResponses: 0,
        averageSessionDuration: 0,
        totalPracticeTime: 0
      });
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/addminn');
    } catch (error) {
      console.error('Failed to log out:', error);
    }
  };

  const handleSendEmailVerification = async () => {
    try {
      if (auth.currentUser) {
        await sendEmailVerification(auth.currentUser);
        setEmailVerificationSent(true);
        setTimeout(() => setEmailVerificationSent(false), 5000);
      }
    } catch (error) {
      console.error('Failed to send verification email:', error);
    }
  };

  const StatCard: React.FC<{ title: string; value: number; color: string; icon: string; subtitle?: string }> = ({ title, value, color, icon, subtitle }) => (
    <div className={`bg-white rounded-lg shadow-md p-6 ${color}`}>
      <div className="flex items-center">
        <div className="flex-shrink-0">
          <div className="text-2xl">{icon}</div>
        </div>
        <div className="ml-4">
          <p className="text-sm font-medium text-gray-600">{title}</p>
          <p className="text-2xl font-bold text-gray-900">{value.toLocaleString()}</p>
          {subtitle && (
            <p className="text-xs text-gray-500 mt-1">{subtitle}</p>
          )}
        </div>
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="loading-spinner mb-4"></div>
          <p className="text-gray-600">Loading admin dashboard...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="bg-red-50 border border-red-200 rounded-md p-4 max-w-md">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-medium text-red-800">Error</h3>
                <div className="mt-2 text-sm text-red-700">
                  <p>{error}</p>
                </div>
                <div className="mt-4">
                  <button
                    onClick={() => window.location.reload()}
                    className="text-sm font-medium text-red-600 hover:text-red-500"
                  >
                    Refresh Page
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const dashboardContent = (
    <div className="min-h-screen bg-gray-50">
      <nav className="bg-red-600 shadow-lg">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between h-16">
            <div className="flex items-center">
              <div className="flex items-center">
                <div className="text-3xl mr-3">🔒</div>
                <h1 className="text-xl font-bold text-white">
                  Admin Dashboard
                </h1>
              </div>
            </div>
            <div className="flex items-center space-x-4">
              <span className="text-sm text-white/90 font-medium">
                Welcome, {currentUser?.email}
              </span>
              {currentUser && !currentUser.emailVerified && (
                <button
                  onClick={handleSendEmailVerification}
                  className="bg-yellow-600 hover:bg-yellow-700 text-white px-3 py-1 rounded-md text-xs font-medium transition-all duration-200"
                  title="Send email verification"
                >
                  {emailVerificationSent ? 'Sent!' : 'Verify Email'}
                </button>
              )}
              <button
                onClick={handleLogout}
                className="bg-white/20 hover:bg-white/30 text-white px-4 py-2 rounded-md text-sm font-medium transition-all duration-200"
              >
                Logout
              </button>
            </div>
          </div>
        </div>
      </nav>

      <main className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
        <div className="px-4 py-6 sm:px-0">
          <div className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-2">
              System Overview
            </h2>
            <p className="text-gray-600">
              Monitor system performance and user activity
            </p>
          </div>

          {/* User Statistics */}
          <div className="mb-8">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">User Statistics</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6">
              <StatCard
                title="Total Users"
                value={userStats.totalUsers}
                color="border-l-4 border-blue-500"
                icon="👥"
              />
              <StatCard
                title="Students"
                value={userStats.students}
                color="border-l-4 border-green-500"
                icon="🎓"
              />
              <StatCard
                title="Teachers"
                value={userStats.teachers}
                color="border-l-4 border-purple-500"
                icon="👨‍🏫"
              />
              <StatCard
                title="Admins"
                value={userStats.admins}
                color="border-l-4 border-red-500"
                icon="🔒"
              />
              <StatCard
                title="Active Users"
                value={userStats.activeUsers}
                color="border-l-4 border-yellow-500"
                icon="🟢"
              />
            </div>
          </div>

          {/* System Statistics */}
          <div className="mb-8">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">System Statistics</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <StatCard
                title="Total Lessons"
                value={systemStats.totalLessons}
                subtitle={`${systemStats.totalSavedSessions} saved sessions`}
                color="border-l-4 border-indigo-500"
                icon="📚"
              />
              <StatCard
                title="Practice Sessions"
                value={systemStats.totalPracticeSessions}
                subtitle={`${systemStats.totalPracticeTime}m total practice time`}
                color="border-l-4 border-orange-500"
                icon="🎵"
              />
              <StatCard
                title="Awards Given"
                value={systemStats.totalAwards}
                subtitle="From user achievements"
                color="border-l-4 border-yellow-500"
                icon="🏆"
              />
              <StatCard
                title="Tip Responses"
                value={systemStats.totalTipResponses}
                subtitle={`Avg: ${systemStats.averageSessionDuration}m/session`}
                color="border-l-4 border-green-500"
                icon="💡"
              />
            </div>
          </div>

          {/* Admin Actions */}
          <div className="bg-white rounded-lg shadow-md p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Admin Actions</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <button
                onClick={loadStats}
                className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md text-sm font-medium transition-colors"
              >
                Refresh Stats
              </button>
              <button
                className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-md text-sm font-medium transition-colors"
                onClick={() => setCurrentView('users')}
              >
                Manage Users
              </button>
              <button
                className="bg-purple-600 hover:bg-purple-700 text-white px-4 py-2 rounded-md text-sm font-medium transition-colors"
                onClick={() => setCurrentView('settings')}
              >
                System Settings
              </button>
              <button
                className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-md text-sm font-medium transition-colors"
                onClick={() => setCurrentView('logs')}
              >
                View Logs
              </button>
              <button
                className="bg-yellow-600 hover:bg-yellow-700 text-white px-4 py-2 rounded-md text-sm font-medium transition-colors"
                onClick={() => setCurrentView('leaderboardPoints')}
              >
                🏆 Points System
              </button>
              <button
                className="bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 rounded-md text-sm font-medium transition-colors"
                onClick={() => setCurrentView('leaderboard')}
              >
                📊 Leaderboard
              </button>
            </div>
          </div>

          {/* Security Notice */}
          <div className="mt-8 bg-red-50 border border-red-200 rounded-md p-4">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-medium text-red-800">
                  Security Notice
                </h3>
                <div className="mt-2 text-sm text-red-700">
                  <p>
                    This is a restricted administrative area. All actions are logged and monitored.
                    Unauthorized access is prohibited and may result in legal action.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );

  // Render different views based on currentView state
  if (currentView === 'users') {
    return <AdminUserManagement onBack={() => setCurrentView('dashboard')} />;
  }

  if (currentView === 'logs') {
    return <AdminLogs onBack={() => setCurrentView('dashboard')} />;
  }

  if (currentView === 'settings') {
    return <AdminSettings onBack={() => setCurrentView('dashboard')} />;
  }

  if (currentView === 'leaderboardPoints') {
    return <LeaderboardPoints />;
  }

  if (currentView === 'leaderboard') {
    return <Leaderboard />;
  }

  return dashboardContent;
};

export default AdminDashboard;
